import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _ from "lodash";

import { DEFAULT_BOOKED_LIST_VIEW } from "@/Constants/common";
import {
  _getBookedCandidates,
  _getBookedMergeContractShifts,
  _getBookedSelectedMergeContractShifts,
  _getBookedShifts,
} from "@/services/clientcandidates";
import {
  handleAPICatch,
  showErrorToast,
  showSuccessToast,
} from "@/utilities/utils";

import {
  formatBookedWorkedCandidates,
  formatExpandedShifts,
} from "../../utilities/dataFormatter";

const initialState = {
  isLoading: false,
  isDashboardUpcomingListLoading: false,
  isPaginationLoading: false,
  bookedCandidates: [],
  selectedCandidate: [], // list of candidate email
  selectedContract: [], // list of contract id
  selectedContractRefNo: [],
  mergeContract: [],
  selectedMergeContract: [],
  showMergeContractModal: false,
  filters: {
    search: "",
    sessionType: [],
    candidateReferenceNo: "",
    contractReferenceNo: "",
    candidateId: [],
    candidateRole: null,
    complianceStatus: null,
    miles: null,
    candidateLicenseNo: "",
    candidatePostcode: "",
    bookedDateFrom: "",
    bookedDateTo: "",
  },
  sorting: {
    sortByColumn: "",
    sortByOrder: "",
  },
  totalCount: 0,
  isSelectedAll: false,
  listView: DEFAULT_BOOKED_LIST_VIEW,
  oldFilters: {},
  pagination: {
    currentPage: 1,
    totalPage: 1,
    isLastPage: false,
  },
  toggleSelected: false,
  showFilters: false,
};

export const fetchBookedCandidates = createAsyncThunk(
  "candidatesBooked/fetchBookedCandidates",
  async (data, { dispatch, getState }) => {
    const {
      clientCandidatesBooked: {
        filters,
        pagination,
        sorting,
        listView,
        bookedCandidates,
      },
    } = getState();
    if (_.isEmpty(data)) {
      data = {
        ...filters,
      };
    }
    if (pagination.isLastPage) {
      return dispatch(setBookedCandidatesLoading(false));
    }

    dispatch(setPaginationLoading(pagination.currentPage !== 1));
    dispatch(toggleSelectedAll(false));
    const _data = {
      ...data,
      currentPage: pagination.currentPage,
      ...sorting,
      listView,
    };
    const response = _getBookedCandidates(_data);
    response
      .then((res) => {
        const {
          data: { booked_list, meta },
        } = res;
        const { currentPage, lastPage, isLastPage, total: totalCount } = meta;
        const data = formatBookedWorkedCandidates(booked_list, "booked");
        dispatch(
          updatePagination({
            currentPage: currentPage + 1,
            totalPage: lastPage,
            isLastPage: isLastPage,
          }),
        );
        let prevList = currentPage === 1 ? [] : _.uniq(bookedCandidates);
        let firstContractId = data.length > 0 ? data[0].contract_id : null;
        dispatch(updateTotalCount(totalCount));
        if (
          prevList.length > 0 &&
          prevList.find((item) => item.contract_id === firstContractId) !==
            undefined
        ) {
          //
        } else {
          dispatch(updateBookedCandidatesList([...prevList, ...data]));
        }
        return res;
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setPaginationLoading(false)); // Set pagination loading to true
          dispatch(setBookedCandidatesLoading(false));
        }, 10);
      });
  },
);

export const fetchBookedShifts = createAsyncThunk(
  "candidatesBooked/fetchBookedShifts",
  async (data, thunkAPI) => {
    const { contractId } = data;
    const response = _getBookedShifts(data);
    response
      .then((res) => {
        const {
          data: { booked_contract_shift_list },
        } = res;
        const data = formatExpandedShifts(booked_contract_shift_list);
        thunkAPI.dispatch(
          updateBookedShifts({
            contract_id: contractId,
            shifts: data,
          }),
        );
      })
      .catch((error) => {
        // handleAPICatch(error);
        return thunkAPI.rejectWithValue(error);
      });
  },
);

export const fetchBookedMergeContractShifts = createAsyncThunk(
  "candidatesBooked/fetchBookedMergeContractShifts",
  async (data, { dispatch }) => {
    const { contractId, loading, setLoading } = data;

    if (loading) return;
    setLoading(true);
    const response = _getBookedMergeContractShifts({ contractId: contractId });
    response
      .then((res) => {
        const {
          data: { all_contracts: data },
        } = res;
        if (!data.length) {
          showErrorToast(
            "No merge contract found for this candidate. please select another candidate.",
          );
          return;
        }

        dispatch(updateMergeContract(data));
        dispatch(toggleMergeContractModal(true));
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setLoading(false);
      });
  },
);

export const updateBookedMergeContracts = createAsyncThunk(
  "candidatesBooked/updateBookedMergeContracts",
  async (data, { dispatch }) => {
    const { contract_ids, callback, loading, setLoading } = data;
    if (loading) return;
    setLoading(true);

    const response = _getBookedSelectedMergeContractShifts({ contract_ids });
    response
      .then((res) => {
        const {
          data: { message },
        } = res;
        // dispatch(toggleMergeContractModal(false))
        showSuccessToast(message);
        dispatch(updateSelectedMergeContract([]));
        dispatch(deSelectAllCandidate());
        callback && callback();
      })
      .catch((error) => {
        handleAPICatch(error);
        return rejectWithValue(error);
      })
      .finally(() => {
        setLoading(false);
      });
  },
);

const candidatesBookedSlice = createSlice({
  name: "candidatesBooked",
  initialState,
  reducers: {
    /**
     * Updates the booked candidates list in the state with the provided payload.
     *
     * @param {object} state - The current state object
     * @param {object} action - The action object containing the payload
     */
    updateBookedCandidatesList: (state, action) => {
      state.bookedCandidates = action.payload;
    },
    setPaginationLoading(state, action) {
      state.isPaginationLoading = action.payload; // New reducer for pagination loading
    },

    /**
     * Updates the selected candidate in the state.
     *
     * @param {Object} state - the current state object
     * @param {Object} action - the action object containing the payload
     * @return {void}
     */
    updateSelectedCandidate: (state, action) => {
      state.selectedCandidate = action.payload;
      if (action.payload.length === 0) {
        state.isSelectedAll = false;
      }
    },
    updateSelectedContract: (state, action) => {
      state.selectedContract = action.payload;
    },
    updateSelectedContractRefNo: (state, action) => {
      state.selectedContractRefNo = action.payload;
    },
    /**
     * Sets the loading state for booked candidates.
     *
     * @param {type} paramName - description of parameter
     * @return {type} description of return value
     */
    setBookedCandidatesLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    //
    /**
     * Updates the filters in the state based on the provided action payload.
     *
     * @param {object} state - The current state object
     * @param {object} action - The action object containing payload for filter updates
     * @return {void}
     */
    updateFilters: (state, action) => {
      if (_.isEmpty(action.payload)) {
        state.filters = {
          search: state.filters.search,
          ...initialState.filters,
        };
        return;
      } else {
        if (action.payload.reset) {
          state.filters = initialState.filters;
        } else {
          state.filters = { ...state.filters, ...action.payload };
        }
      }
    },
    updateMergeContract: (state, action) => {
      state.mergeContract = action.payload;
    },
    updateSelectedMergeContract: (state, action) => {
      state.selectedMergeContract = action.payload;
    },
    toggleMergeContractModal: (state, action) => {
      state.showMergeContractModal = action.payload;
    },
    updateSorting: (state, action) => {
      state.sorting = action.payload;
    },
    /**
     * Deselects all candidates in the given state.
     *
     * @param {objecupdateBookedShiftst} state - the current state
     * @param {object} action - the action being performed
     * @return {void}
     */
    deSelectAllCandidate: (state, action) => {
      state.selectedCandidate = [];
      state.selectedContract = [];
      state.isSelectedAll = false;
      state.toggleSelected = !state.toggleSelected;
    },
    /**
     * A function to show the filter modal and update state accordingly.
     *
     * @param {object} state - The current state object
     * @param {object} action - The action object triggering the function
     */
    showFilterModal: (state, action) => {
      if (action.payload) {
        state.oldFilters = state.filters;
      }
      state.showFilters = action.payload;
    },
    /**
     * Update the pagination state with the given action payload.
     *
     * @param {type} state - The current state
     * @param {type} action - The action payload
     * @return {type} undefined
     */
    updatePagination: (state, action) => {
      state.pagination = action.payload;
    },
    /**
     * Updates the state filters with the old filters.
     *
     * @param {object} state - The current state object
     * @param {object} action - The action object
     * @return {void}
     */
    reStateFilters: (state, action) => {
      state.filters = state.oldFilters;
    },
    updateToggleSelected: (state, action) => {
      state.toggleSelected = !state.toggleSelected;
    },
    updateListView: (state, action) => {
      state.listView = action.payload;
    },
    updateBookedShifts: (state, action) => {
      const { contract_id, shifts } = action.payload;
      let bookedClone = [...state.bookedCandidates];
      state.bookedCandidates = bookedClone.map((ele) => {
        if (ele.contract_id == contract_id) {
          ele.shifts = shifts;
        }
        return ele;
      });
    },
    updateTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    toggleSelectedAll: (state, action) => {
      state.isSelectedAll = action.payload;
    },
    updateCandidateStatus: (state, action) => {
      let { unsuitable, sortlisted, isSelectedAll, ignoreIds, ids } =
        action.payload;
      let _ids = ids.map((id) => id.toString());
      let _ignoreIds = ignoreIds.map((id) => id.toString());
      state.bookedCandidates = state.bookedCandidates.map((candidate) => {
        if (_ignoreIds.includes(candidate?.candidate_id?.toString())) {
          return candidate;
        }
        if (
          _ids.includes(candidate?.candidate_id?.toString()) ||
          isSelectedAll
        ) {
          candidate.candidate_unsuitable = unsuitable;
          candidate.candidate_sortlisted = sortlisted;
        }
        return candidate;
      });
    },
  },
});

export const {
  updateBookedCandidatesList,
  setBookedCandidatesLoading,
  updateSelectedCandidate,
  updateSelectedContract,
  updateSelectedContractRefNo,
  updateFilters,
  updateMergeContract,
  updateSelectedMergeContract,
  toggleMergeContractModal,
  updateSorting,
  deSelectAllCandidate,
  showFilterModal,
  updatePagination,
  reStateFilters,
  updateToggleSelected,
  updateListView,
  updateBookedShifts,
  updateTotalCount,
  setPaginationLoading,
  toggleSelectedAll,
  updateCandidateStatus,
} = candidatesBookedSlice.actions;

export default candidatesBookedSlice.reducer;
